import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Me } from './api';

type State = { status: 'loading' } | { status: 'setup'; needsToken?: boolean } | { status: 'anon'; expired?: boolean } | { status: 'in'; me: Me };
interface AuthCtx { state: State; login: (email: string, password: string) => Promise<void>; logout: () => Promise<void>; refresh: () => Promise<void>; setMe: (m: Me) => void }
const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try { const me = await api.get<Me>('/api/auth/me'); setState({ status: 'in', me }); }
    catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        const s = await api.get<{ configured: boolean; needsSetupToken?: boolean }>('/api/auth/status').catch(() => ({ configured: true, needsSetupToken: false }));
        setState(s.configured ? { status: 'anon' } : { status: 'setup', needsToken: !!s.needsSetupToken });
      } else setState({ status: 'anon' });
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const onExpired = () => { qc.clear(); setState({ status: 'anon', expired: true }); };
    window.addEventListener('sp:sessao-expirada', onExpired);
    return () => window.removeEventListener('sp:sessao-expirada', onExpired);
  }, [qc]);

  const login = useCallback(async (email: string, password: string) => { const me = await api.post<Me>('/api/auth/login', { email, password }); qc.clear(); setState({ status: 'in', me }); }, [qc]);
  const logout = useCallback(async () => { await api.post('/api/auth/logout').catch(() => null); qc.clear(); setState({ status: 'anon' }); }, [qc]);
  const setMe = useCallback((me: Me) => setState({ status: 'in', me }), []);
  return <Ctx.Provider value={{ state, login, logout, refresh, setMe }}>{children}</Ctx.Provider>;
}
export function useAuth() { const c = useContext(Ctx); if (!c) throw new Error('AuthProvider ausente'); return c; }
export function useMe(): Me { const { state } = useAuth(); if (state.status !== 'in') throw new Error('Sem sessão'); return state.me; }
export const isAdmin = (me: Me) => me.globalRole === 'platform_admin' || me.globalRole === 'org_admin';
